import { useEffect, useRef, useState } from 'react';
import { type CurrencySymbols } from '@/features/currency/types/currency-types';
import getListings from '@/features/listing/requests/get-listings';
import { PaginationNames } from '@/features/listing/types/listing-filters-types';
import { type ListingsCollection, type ListingType } from '@/features/listing/types/listing-types';
import prepareListingsSearchParams from '@/features/listing/utils/prepare-listings-search-params';
import { type Place } from '@/features/place/types/place-types';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
export default function useLoadListings(listingType: ListingType, placeBbox: Place['bbox'], currencyName: CurrencySymbols['name']) {
  const listingsPerPage = 3;
  const bearerToken = getUserBearerCookie();
  const [loading, setLoading] = useState(false);
  const [listings, setListings] = useState<ListingsCollection['items']>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [error, setError] = useState<Error>();
  const isLoadingRef = useRef(false);
  const currentPageRef = useRef(1);
  const hasNextPageRef = useRef(true);
  const loadedListingIdsRef = useRef(new Set<number>());
  useEffect(() => {
    setListings([]);
    setLoading(false);
    setHasNextPage(true);
    setCurrentPage(1);
    setError(undefined);
    isLoadingRef.current = false;
    currentPageRef.current = 1;
    hasNextPageRef.current = true;
    loadedListingIdsRef.current = new Set<number>();
  }, [currencyName, listingType, placeBbox]);
  async function loadMoreListings() {
    if (isLoadingRef.current || !hasNextPageRef.current) {
      return;
    }
    isLoadingRef.current = true;
    setLoading(true);
    try {
      const searchParams = new URLSearchParams();
      const requestedPage = currentPageRef.current;
      searchParams.set(PaginationNames.PageNumber, String(requestedPage));
      searchParams.set(PaginationNames.PageSize, String(listingsPerPage));
      const listingSearchParams = prepareListingsSearchParams(searchParams, listingType, placeBbox);
      const listingsCollection = await getListings(listingSearchParams.toString(), currencyName, bearerToken);
      const totalPages = Math.ceil(listingsCollection.count / listingsPerPage);
      if (requestedPage === 1 && listingsCollection.count > 0) {
        listingsCollection.items.shift();
        listingsCollection.count -= 1;
      }
      const nextItems = listingsCollection.items.filter(listing => {
        if (loadedListingIdsRef.current.has(listing.id)) {
          return false;
        }
        loadedListingIdsRef.current.add(listing.id);
        return true;
      });
      const nextPage = requestedPage + 1;
      const shouldHaveNextPage = totalPages > requestedPage && listingsCollection.count > 0;
      currentPageRef.current = nextPage;
      hasNextPageRef.current = shouldHaveNextPage;
      setCurrentPage(nextPage);
      setListings(current => [...current, ...nextItems]);
      setHasNextPage(shouldHaveNextPage);
    } catch (error_) {
      hasNextPageRef.current = false;
      setError(error_ instanceof Error ? error_ : new Error('Something went wrong'));
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }
  return {
    error,
    hasNextPage,
    listings,
    loading,
    loadMoreListings
  };
}